import { useDependency } from 'ditox-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScriptType,
  transformSpecialScripts,
} from '@/components/conversations/conversation-list/message-list-item/message-content/transform-scripts';
import { MESSAGE_MANAGER_TOKEN } from '@/container';
import useUserDisplayInfo from '@/hooks/users/use-user-display-info';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type {
  ConversationSessionsTypeUnion,
  SessionLastMessageType,
} from '@/models/conversations/conversation-session';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { usePeriodicMessageStore } from '@/stores/periodic-message';
import { isDeletedById } from '@/utils/check-messages-status';
import { generateSessionKey } from '@/utils/session';
import { stripHtmlTags } from '@/utils/strip-html-tags';
import { transformMessageTextByType } from '@/utils/transform-message-text-by-type';

interface Props {
  session: ConversationSessionsTypeUnion;
}

export default function SessionListItemContent({ session }: Props) {
  const { t } = useTranslation();
  const messageManager = useDependency(MESSAGE_MANAGER_TOKEN);

  const { ID, UserID, SessionType, LastMsgID, LastMsg, IsNewSession } = session;
  const isGroup = SessionType === 'group';
  const key = generateSessionKey(SessionType, ID);

  const draft = useConversationSessionStore((s) => s.drafts[key]);
  const isLastMessageDeleted = usePeriodicMessageStore((s) => isDeletedById(key, LastMsgID, s));
  const [lastMessage, setLastMessage] = useState<SessionLastMessageType>(() => session);

  useEffect(() => {
    if (isLastMessageDeleted) {
      (async () => {
        const deletedIds = usePeriodicMessageStore.getState().deletedMessageIds[key] ?? [];
        if (deletedIds.length) {
          const lastValid = await messageManager.dispatch('getLastValidMessage', SessionType, ID, deletedIds);
          if (lastValid) {
            setLastMessage({
              UserNickName: lastValid.UserNickName,
              LastMsgID: lastValid.ID,
              LastMsg: lastValid.Message,
              LastMsgTimeStamp: lastValid.TimeStamp,
              LastMsgType: lastValid.MsgType,
              LastAutoDeleteTime: lastValid.AutoDeleteTime,
              LastReadDelete: lastValid.ReadDelete,
              LastReadStamp: lastValid.ReadStamp,
            });
          }
        }
      })();
    } else {
      setLastMessage(session);
    }
  }, [isLastMessageDeleted, ID, SessionType, LastMsgID, key]);
  const { name: senderName } = useUserDisplayInfo(UserID, {
    defaultName: lastMessage?.UserNickName || (isGroup ? session.UserNickName : session.NickName),
  });

  if (draft) {
    if (draft.Quote) {
      const { Content, Quote } = draft;

      return (
        <span className='italic'>
          {`(${t('conversation.quote')} ${Quote?.UserDisplayName || Quote?.UserNickName || Quote?.UserID})`}
          {Content && ` :${Content}`}
        </span>
      );
    }

    return stripHtmlTags(draft?.Content || '');
  }

  if (LastMsgID === 0) {
    return (
      <span className='italic'>
        {isGroup ? t('conversation.group.welcome') : t('conversation.message.start-messaging')}
      </span>
    );
  }

  let message: ReactNode = lastMessage?.LastMsg ?? LastMsg;

  if (lastMessage?.LastMsgType === ConversationMessageContentType.Text) {
    message = transformSpecialScripts(lastMessage.LastMsgID, lastMessage.LastMsg, [], [ScriptType.Emoticon]);
  } else if (lastMessage?.LastMsgType) {
    message = transformMessageTextByType(
      {
        MsgType: lastMessage.LastMsgType,
        Message: lastMessage.LastMsg,
      },
      senderName,
      t,
    );
  }

  if (isGroup && senderName && lastMessage?.LastMsgType !== ConversationMessageContentType.JoinGroup) {
    message = `${senderName}：${message}`;
  }

  return message;
}
